// ==================== REUSABLE MODAL COMPONENT ====================

import { useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '../../utils/helpers';

// ==================== TYPES ====================

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps {
  /** Controls modal visibility */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal content */
  children: ReactNode;
  /** Modal title (optional) */
  title?: ReactNode;
  /** Modal subtitle (optional) */
  subtitle?: ReactNode;
  /** Modal size */
  size?: ModalSize;
  /** Show close button in header */
  showCloseButton?: boolean;
  /** Close modal when clicking backdrop */
  closeOnBackdropClick?: boolean;
  /** Close modal when pressing Escape */
  closeOnEscape?: boolean;
  /** Custom header content (replaces default header) */
  customHeader?: ReactNode;
  /** Custom footer content */
  footer?: ReactNode;
  /** Additional class name for modal container */
  className?: string;
  /** Additional class name for modal content */
  contentClassName?: string;
  /** Disable body scroll when modal is open */
  preventBodyScroll?: boolean;
}

// ==================== SIZE CLASSES ====================

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)]',
};

// ==================== MODAL COMPONENT ====================

const Modal = ({
  isOpen,
  onClose,
  children,
  title,
  subtitle,
  size = 'md',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  customHeader,
  footer,
  className,
  contentClassName,
  preventBodyScroll = true,
}: ModalProps) => {
  // Handle escape key
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape) {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  // Handle backdrop click
  const handleBackdropClick = useCallback(() => {
    if (closeOnBackdropClick) {
      onClose();
    }
  }, [closeOnBackdropClick, onClose]);

  // Add/remove escape listener and body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      if (preventBodyScroll) {
        document.body.style.overflow = 'hidden';
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (preventBodyScroll) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, handleEscape, preventBodyScroll]);

  // Check if we have a header to show
  const hasHeader = customHeader || title || showCloseButton;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleBackdropClick}
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={cn(
              'relative w-full overflow-hidden',
              'bg-(--color-charcoal-black) border border-(--color-border-gray)',
              'rounded-2xl shadow-2xl',
              sizeClasses[size],
              className
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            {hasHeader && (
              <div className="flex items-center justify-between px-6 py-4 border-b border-(--color-border-gray)">
                {customHeader ? (
                  customHeader
                ) : (
                  <div className="flex-1 min-w-0">
                    {title && (
                      <h2 className="text-lg font-semibold text-white truncate">
                        {title}
                      </h2>
                    )}
                    {subtitle && (
                      <p className="text-sm text-(--color-muted-gray) truncate mt-0.5">
                        {subtitle}
                      </p>
                    )}
                  </div>
                )}
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="p-2 rounded-lg text-(--color-muted-gray) hover:text-white hover:bg-white/5 transition-colors ml-4 shrink-0"
                    aria-label="Close modal"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            )}

            {/* Content */}
            <div
              className={cn(
                'overflow-y-auto',
                size === 'full' ? 'max-h-[calc(100vh-10rem)]' : 'max-h-[calc(90vh-8rem)]',
                contentClassName
              )}
            >
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div className="px-6 py-4 border-t border-(--color-border-gray) bg-(--color-charcoal-black)">
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

// ==================== MODAL HEADER COMPONENT ====================

interface ModalHeaderProps {
  children: ReactNode;
  className?: string;
}

const ModalHeader = ({ children, className }: ModalHeaderProps) => (
  <div className={cn('px-6 py-4', className)}>{children}</div>
);

// ==================== MODAL BODY COMPONENT ====================

interface ModalBodyProps {
  children: ReactNode;
  className?: string;
}

const ModalBody = ({ children, className }: ModalBodyProps) => (
  <div className={cn('px-6 py-4', className)}>{children}</div>
);

// ==================== MODAL FOOTER COMPONENT ====================

interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

const ModalFooter = ({ children, className }: ModalFooterProps) => (
  <div className={cn('flex items-center justify-end gap-3', className)}>
    {children}
  </div>
);

// ==================== EXPORTS ====================

export { Modal, ModalHeader, ModalBody, ModalFooter };

